import { FilesRailDraggableFileRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDraggableFileRow";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { FilesRailFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFolderRow";
import type { FilesRailOsDropAtPath } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { WorkspacesRailCanvasNode } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailCanvasNode";
import {
  DERIVED_FOLDER_ID_PREFIX,
  getDirectChildren,
} from "@/pages/workspaces/utils/folderPaths";
import { getFileRecordName } from "@/panes/file-record-detail/contentType";
import type { Canvas, FileRecord } from "@cohesive/api-client";
import { useMemo } from "react";

/**
 * Row actions for a canvas in the tree. Share and Archive are present only
 * when the user holds the right — the row shows a verb if its callback exists.
 */
export type FilesRailCanvasActions = (canvas: Canvas) => {
  onRename: (canvas: Canvas) => void;
  onShare?: (canvas: Canvas) => void;
  onDelete?: (canvas: Canvas) => void;
};

/**
 * Folders can exist purely as deeper files' path prefixes — the storage
 * ingest and API writers create records by written path, with no explicit
 * trailing-slash folder row (an agent writing `reports/x.md` in the sandbox
 * is the canonical case). Derive the missing rung so those files stay
 * reachable, mirroring the destination cascade's prefix derivation. Derived rows
 * carry a sentinel id: they render and accept drops but get no row menu
 * (there is no record to rename/move/archive).
 */
function deriveMissingFolders(
  records: FileRecord[],
  parentPath: string,
  explicit: FileRecord[],
): FileRecord[] {
  const present = new Set(explicit.map((r) => r.path));
  const derived = new Map<string, FileRecord>();
  for (const r of records) {
    if (r.status === "pending" || r.status === "deleted") continue;
    if (!r.path.startsWith(parentPath)) continue;
    const rest = r.path.slice(parentPath.length);
    const slash = rest.indexOf("/");
    // Only records at least one level deeper imply a folder rung here; a
    // trailing-slash direct child is an explicit folder row, not evidence.
    if (slash <= 0 || slash === rest.length - 1) continue;
    const folderPath = parentPath + rest.slice(0, slash + 1);
    if (present.has(folderPath) || derived.has(folderPath)) continue;
    derived.set(folderPath, {
      id: `${DERIVED_FOLDER_ID_PREFIX}${folderPath}`,
      path: folderPath,
      status: "processed",
      class: "workspace",
      kind: "folder",
    } as FileRecord);
  }
  return [...derived.values()];
}

/**
 * Folders first, then canvases and files together in name order — one run, so
 * a canvas and a file sitting in the same folder read as what they are: two
 * things in the same place.
 */
export function sortDirectChildren(records: FileRecord[], parentPath: string) {
  const children = getDirectChildren(records, parentPath).filter(
    (r) => r.status !== "pending" && r.status !== "deleted",
  );
  const explicitFolders = children.filter((r) => r.path.endsWith("/"));
  const folders = [
    ...explicitFolders,
    ...deriveMissingFolders(records, parentPath, explicitFolders),
  ];
  // A canvas row without its canvas cannot be drawn as anything — its path
  // leaf is the canvas id, so falling through to the file row would show an
  // "Untitled" file that opens nothing. The server attaches the canvas to
  // every row it returns; a row arriving without one means an API that
  // predates canvas rows (returns them without filtering) — drop it.
  const leaves = children.filter(
    (r) => !r.path.endsWith("/") && (r.kind !== "canvas" || !!r.canvas),
  );
  const byName = (a: FileRecord, b: FileRecord) =>
    getFileRecordName(a).localeCompare(getFileRecordName(b), undefined, {
      sensitivity: "base",
    });
  folders.sort(byName);
  leaves.sort(byName);
  return [...folders, ...leaves];
}

function dragNamespaceFor(workspaceId: string, parentPath: string) {
  return parentPath
    ? `files-rail:${workspaceId}:${parentPath}`
    : `files-rail:${workspaceId}:root`;
}

export function FilesRailTree({
  records,
  workspaceId,
  parentPath = "",
  activeFileRecordId,
  activeCanvasId = null,
  canvasActions,
  onOpenFile,
  menuActions,
  getOsDropHandler,
  onUploadToPath,
  onUploadFolderToPath,
  onCreateDocumentAtPath,
  onCreateSubfolder,
  onCreateCanvasAtPath,
}: {
  records: FileRecord[];
  workspaceId: string;
  parentPath?: string;
  activeFileRecordId: string | null;
  /** Canvas currently open, so its row reads as active. */
  activeCanvasId?: string | null;
  /** Row actions for the canvases filed in this tree. */
  canvasActions: FilesRailCanvasActions;
  onOpenFile: (record: FileRecord) => void;
  menuActions?: FilesRailFileMenuActions;
  getOsDropHandler?: FilesRailOsDropAtPath;
  onUploadToPath?: (parentPath: string) => void;
  onUploadFolderToPath?: (parentPath: string) => void;
  onCreateDocumentAtPath?: (parentPath: string) => void;
  onCreateSubfolder?: (parentPath: string) => void;
  onCreateCanvasAtPath?: (parentPath: string) => void;
}) {
  const direct = useMemo(
    () => sortDirectChildren(records, parentPath),
    [records, parentPath],
  );

  if (direct.length === 0) {
    if (parentPath) {
      return <p className="px-2 py-1 text-sm text-n-40">No files in folder.</p>;
    }
    return null;
  }

  return (
    <>
      {direct.map((record) => {
        if (record.path.endsWith("/")) {
          return (
            <FilesRailFolderRow
              key={record.id}
              record={record}
              workspaceId={workspaceId}
              onOsDrop={getOsDropHandler?.(record.path) ?? null}
              menuActions={menuActions}
              onUploadToPath={onUploadToPath}
              onUploadFolderToPath={onUploadFolderToPath}
              onCreateDocumentAtPath={onCreateDocumentAtPath}
              onCreateSubfolder={onCreateSubfolder}
              onCreateCanvasAtPath={onCreateCanvasAtPath}
            >
              <FilesRailTree
                records={records}
                workspaceId={workspaceId}
                parentPath={record.path}
                activeFileRecordId={activeFileRecordId}
                activeCanvasId={activeCanvasId}
                canvasActions={canvasActions}
                onOpenFile={onOpenFile}
                menuActions={menuActions}
                getOsDropHandler={getOsDropHandler}
                onUploadToPath={onUploadToPath}
                onUploadFolderToPath={onUploadFolderToPath}
                onCreateDocumentAtPath={onCreateDocumentAtPath}
                onCreateSubfolder={onCreateSubfolder}
                onCreateCanvasAtPath={onCreateCanvasAtPath}
              />
            </FilesRailFolderRow>
          );
        }

        if (record.kind === "canvas" && record.canvas) {
          return (
            <WorkspacesRailCanvasNode
              key={record.id}
              canvas={record.canvas}
              dragNamespace={dragNamespaceFor(workspaceId, parentPath)}
              sourcePath={parentPath}
              active={activeCanvasId === record.canvas.id}
              {...canvasActions(record.canvas)}
            />
          );
        }

        return (
          <FilesRailDraggableFileRow
            key={record.id}
            record={record}
            workspaceId={workspaceId}
            dragNamespace={dragNamespaceFor(workspaceId, parentPath)}
            active={activeFileRecordId === record.id}
            onOpen={() => onOpenFile(record)}
            menuActions={menuActions}
          />
        );
      })}
    </>
  );
}
